import { useCallback, useEffect } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import {
  whatsappEndpointService,
  type WhatsAppEndpointContactsResult,
} from "../services/whatsappEndpointService";

const EMPTY_CONTACTS: fhir5.ContactPoint[] = [];

export interface UseWhatsAppEndpointContactsResult {
  endpointId?: string;
  contacts: fhir5.ContactPoint[];
  isLoading: boolean;
  errorMessage: string | null;
  isSaving: boolean;
  saveReorderedContacts: (input: {
    endpointId: string;
    contacts: fhir5.ContactPoint[];
  }) => Promise<void>;
  refetch: () => void;
}

function mapErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Não foi possível carregar os dados do WhatsApp.";
}

export function useWhatsAppEndpointContacts(
  organizationId?: string,
): UseWhatsAppEndpointContactsResult {
  const loadTask = useCallback(
    async (targetOrganizationId: string) =>
      whatsappEndpointService.listContactsByOrganizationId(targetOrganizationId),
    [],
  );

  const {
    run: loadContacts,
    loading: isLoading,
    data: loadedContactsResult,
    error,
  } = useAsyncTask<[string], WhatsAppEndpointContactsResult, string>({
    task: loadTask,
    mapError: mapErrorMessage,
  });

  const saveTask = useCallback(
    async (input: { endpointId: string; contacts: fhir5.ContactPoint[] }) => {
      await whatsappEndpointService.reorderContactsByEndpointId(input);
      return undefined;
    },
    [],
  );

  const {
    run: saveContacts,
    loading: isSaving,
  } = useAsyncTask<
    [
      {
        endpointId: string;
        contacts: fhir5.ContactPoint[];
      },
    ],
    void,
    string
  >({
    task: saveTask,
    mapError: mapErrorMessage,
  });

  const endpointId = loadedContactsResult?.endpointId;
  const contacts = loadedContactsResult?.contacts ?? EMPTY_CONTACTS;

  useEffect(() => {
    if (!organizationId) return;
    void loadContacts(organizationId);
  }, [organizationId, loadContacts]);

  return {
    endpointId,
    contacts,
    isLoading,
    errorMessage: error ?? null,
    isSaving,
    saveReorderedContacts: async (input) => {
      await saveContacts(input);
    },
    refetch: () => {
      if (!organizationId) return;
      void loadContacts(organizationId);
    },
  };
}

